/**
 * SidebarPrimaryAdapter — thin wiring layer between Sidebar consumers
 * and the PrimaryRail presentation primitive.
 *
 * SOLID / GRASP:
 *   SRP  — adapter only: threads caller-supplied props into PrimaryRail.
 *          No navigation logic, no routing, no internal state.
 *   OCP  — new items / overrides flow through PrimaryRail's own OCP
 *          extension point; this adapter is untouched.
 *   GRASP — PrimaryRail is the navigation vocabulary expert; this
 *           adapter is the wiring expert.
 *   DRY  — zero re-implementation of PrimaryRail internals.
 *   KISS — no state, no effects, no context: pure prop threading.
 *   SoC  — sits between the Layout consumer and the PrimaryRail primitive.
 *
 * Backward compatibility:
 *   Additive scaffold. Ships unconsumed until a Layout consumer passes
 *   the required props. Existing Sidebar is untouched.
 *
 * exactOptionalPropertyTypes compliant — optional props use `?` only
 * on properties that are truly absent vs. explicitly provided.
 */

import type { ComponentProps } from 'react';
import {
    PrimaryRail,
    DEFAULT_PRIMARY_RAIL_ITEMS,
    type PrimaryRailKey,
    type PrimaryRailItem,
} from './PrimaryRail';

// ── Public types ────────────────────────────────────────────────────────

export type SidebarPrimaryAdapterProps = {
    /** Current active key in the new 5-item rail vocabulary. */
    activeKey: PrimaryRailKey;
    /** Selection handler. Caller owns navigation logic (GRASP). */
    onSelect: (key: PrimaryRailKey) => void;
    /**
     * Optional per-item badge counts keyed by PrimaryRailKey.
     * Threaded into each matching item's `badgeCount` field.
     * Smart-count rule: badge renders only when count > 0.
     */
    badgeCounts?: Partial<Record<PrimaryRailKey, number>>;
    /**
     * Optional role-aware label and/or badge overrides.
     * When provided, merged with badgeCounts (overrides take precedence
     * on badgeCount; caller is the expert for their role context).
     * Unmatched keys are ignored by PrimaryRail (OCP).
     */
    labelOverrides?: ReadonlyArray<PrimaryRailItem>;
    /** When true the rail shrinks to icon-only (72 px). Default: false. */
    collapsed?: boolean;
    /** Root data-testid passthrough to the wrapping <aside>. */
    'data-testid'?: string;
};

// ── Component ──────────────────────────────────────────────────────────

/**
 * Thin adapter: wraps PrimaryRail with an <aside> shell and computes
 * the `items[]` array from `labelOverrides` + `badgeCounts`.
 *
 * If `labelOverrides` is provided it is used as the base for item
 * construction (label + badgeCount from overrides, badgeCount wins
 * over badgeCounts if both specify the same key).
 *
 * If `labelOverrides` is absent, items are synthesised from
 * DEFAULT_PRIMARY_RAIL_ITEMS + badgeCounts so the caller never needs
 * to re-declare labels just to set a badge.
 */
export function SidebarPrimaryAdapter({
    activeKey,
    onSelect,
    badgeCounts,
    labelOverrides,
    collapsed,
    'data-testid': testId,
}: SidebarPrimaryAdapterProps) {
    const items = buildItems(labelOverrides, badgeCounts);

    // exactOptionalPropertyTypes: only spread optional props when they
    // carry a real value — never pass `undefined` to an optional field.
    const optionalProps: Pick<
        ComponentProps<typeof PrimaryRail>,
        'items' | 'collapsed' | 'data-testid'
    > = {};
    if (items.length > 0)      optionalProps.items     = items;
    if (collapsed !== undefined) optionalProps.collapsed = collapsed;

    return (
        <aside
            className="sidebar-primary-adapter"
            data-testid={testId}
        >
            <PrimaryRail
                activeKey={activeKey}
                onSelect={onSelect}
                {...optionalProps}
            />
        </aside>
    );
}

// ── Helpers ─────────────────────────────────────────────────────────────

/**
 * Computes the items[] array passed to PrimaryRail.
 *
 * Strategy:
 *   1. Start from labelOverrides if provided; else from the canonical
 *      DEFAULT_PRIMARY_RAIL_ITEMS (key + default label only).
 *   2. For each item, apply any badgeCounts[key] that is NOT already
 *      expressed as item.badgeCount in labelOverrides — labelOverrides
 *      wins on collision (the caller who supplied an override knows best).
 *   3. Return the merged array. Items with no label override and no
 *      badge count are omitted (PrimaryRail defaults apply for them).
 *
 * Returns an empty array when there is nothing to override, which
 * signals PrimaryRail to use its full defaults (KISS).
 */
function buildItems(
    labelOverrides: ReadonlyArray<PrimaryRailItem> | undefined,
    badgeCounts: Partial<Record<PrimaryRailKey, number>> | undefined,
): PrimaryRailItem[] {
    if (!labelOverrides && !badgeCounts) return [];

    // Build a lookup of labelOverrides by key.
    const overrideMap = new Map<PrimaryRailKey, PrimaryRailItem>();
    if (labelOverrides) {
        for (const item of labelOverrides) {
            overrideMap.set(item.key, item);
        }
    }

    // Keys that appear in either source.
    const allKeys: PrimaryRailKey[] = DEFAULT_PRIMARY_RAIL_ITEMS.map((d) => d.key);

    const result: PrimaryRailItem[] = [];

    for (const key of allKeys) {
        const override = overrideMap.get(key);
        const badgeFromCounts = badgeCounts?.[key];

        // Determine whether we have any material to contribute for this key.
        const hasLabel   = override?.label !== undefined;
        const hasBadge   = override?.badgeCount !== undefined || badgeFromCounts !== undefined;

        if (!hasLabel && !hasBadge) continue;

        // labelOverride.badgeCount wins over badgeCounts[key].
        const resolvedBadge: number | undefined =
            override?.badgeCount !== undefined
                ? override.badgeCount
                : badgeFromCounts;

        const item: PrimaryRailItem = { key };
        if (hasLabel && override?.label !== undefined) {
            item.label = override.label;
        }
        if (resolvedBadge !== undefined) {
            item.badgeCount = resolvedBadge;
        }
        result.push(item);
    }

    return result;
}

export default SidebarPrimaryAdapter;
